/**
 * The patient page.
 *
 * Header widgets: PatientTemporalSlider (project the dossier at a past date) and
 * PatientSearchBar (ask the dossier in natural language).
 *
 * The « Dossier » tab reads the care pathway — episodes, moments, and the ontology hanging off
 * each moment — through `/patient/{tenant}/{id}/pathway`: a field tied to an act shows
 * UNDER its episode. It is rendered by [components/TemporalDossier] from
 * [utils/use-temporal-projection], so the dataset modal shows the same dossier.
 *
 * Data: identity and schema come from `/patient/{tenant_id}/{id_patient}`; CLINICAL fields come
 * only from the dated contribution journal, projected through
 * `/patient/{tenant}/{id}/state?at=`. A value never appears before the document that
 * produced it, and each value is annotated « (au DATE) ».
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@app/components/ui/button";
import { Card, CardContent } from "@app/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@app/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@app/components/ui/popover";
import TemporalDossier from "@app/components/TemporalDossier";
import { SourceDocumentProvider } from "@app/components/SourceDocument";
import { CorrectionLogContext } from "@app/utils/correction-log";
import { PatientTemporalSlider } from "@app/components/PatientTemporalSlider";
import { PatientSearchBar } from "@app/components/PatientSearchBar";
import { useNavigate, useParams, useSearchParams } from "react-router";
import API from "@app/services/api";
import useUser from "@app/zustand/user";
import { isDemoEmail } from "@app/utils/demo-account";
import type { PatientEvent } from "@api/src/types/responses";
import dayjs from "dayjs";
import { PatientGetResponse } from "@api/src/types/responses";
import { MageXPatientWithFeedbacks } from "@api/src/types/patient";
import type { MageXFeedback } from "@api/src/types/patient";
import useMageXStore from "@app/zustand/store";
import { mapPatientToPatientsSelectV2 } from "@app/utils/patient";
import {
  asClinicalDay,
  useTemporalProjection,
} from "@app/utils/use-temporal-projection";
import { Trans, useLingui } from "@lingui/macro";
import { PatientSchema, HeaderLayout } from "@app/types/few-schot-schema";
import {
  Check,
  ClipboardList,
  Database,
  Download,
  FileText,
  Loader2,
  MoreVertical,
  ShieldOff,
  Workflow,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@app/components/ui/dropdown-menu";
import { downloadPost } from "@app/utils/download-file";
import { useReadonlyScope } from "@app/utils/use-readonly-scope";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@app/components/ui/alert-dialog";
import PatientDocuments from "@app/components/PatientDocuments";
import { useIsAdmin } from "@app/utils/use-is-admin";
import { useCanRunPipeline } from "@app/utils/use-can-run-pipeline";
import PipelineView, { type StepOutcome } from "@app/components/PipelineView";
import PatientDatasetBadges from "@app/components/PatientDatasetBadges";
import type { PatientDataset } from "@app/components/PatientDatasetBadges";
import { useEurospineStep } from "@app/utils/use-eurospine-step";
import { usePaginatedSearch } from "@app/utils/use-paginated-search";
import {
  fetchDatasetPickerPage,
  type DatasetPickerItem,
} from "@app/utils/dataset-picker";
import { readRefusal } from "@app/utils/reference-dataset";

const EMPTY_HEADER_LAYOUT: HeaderLayout = {
  badges: [],
};

const DEMO_TENANT_BASE = "demo.calimed.mage-x";
const DEMO_SUPPORTED_LOCALES: ReadonlyArray<string> = ["fr", "en", "de", "es"];

export default function Patient() {
  const params = useParams();
  const patientId = params.id_patient;
  if (!patientId) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-start pt-10 justify-center">
        <Trans>
          Choisissez un patient dans le menu ci-dessus
        </Trans>
      </div>
    );
  }
  return <PatientDetail key={patientId} />;
}

function PatientDetail() {
  const { t, i18n } = useLingui();
  const params = useParams();
  const patientId = params.id_patient;
  const tenantId = params.tenant_id;
  const navigate = useNavigate();
  const user = useUser((state) => state.user);
  const isDemoUser =
    isDemoEmail((user as { email?: string } | null)?.email);

  useEffect(() => {
    if (!isDemoUser || !tenantId) return;
    if (!tenantId.startsWith(`${DEMO_TENANT_BASE}.`)) return;
    const activeLocale = DEMO_SUPPORTED_LOCALES.includes(i18n.locale)
      ? i18n.locale
      : "fr";
    const expectedTenant = `${DEMO_TENANT_BASE}.${activeLocale}`;
    if (tenantId !== expectedTenant) {
      navigate("/dashboard", { replace: true });
    }
  }, [isDemoUser, tenantId, i18n.locale, navigate]);

  const isAdmin = useIsAdmin();
  const canRunPipeline = useCanRunPipeline();
  const readonlyScope = useReadonlyScope();
  const [exporting, setExporting] = useState(false);
  const [confirmOpposition, setConfirmOpposition] = useState(false);
  const [oppositionSubmitting, setOppositionSubmitting] = useState(false);
  // The doc-first reading of this dossier, run from this page.
  const [reading, setReading] = useState(false);
  const [readOutcome, setReadOutcome] = useState<StepOutcome>(null);

  const [schema, setSchema] = useState<PatientSchema>({});
  const [headerLayout, setHeaderLayout] = useState<HeaderLayout | null>(null);
  // The dossier row: identity and dossier columns. Clinical values come from the projection.
  const [patient, setPatient] = useState<MageXPatientWithFeedbacks | null>(null);
  // Read by the correction modal: a correction reopens as it was made (PT-165).
  const [correctionLog, setCorrectionLog] = useState<MageXFeedback[]>([]);

  const effectiveHeaderLayout = headerLayout ?? EMPTY_HEADER_LAYOUT;

  const patientsSelect = useMageXStore((state) => state.patientsSelect);
  const setPatientsSelect = useMageXStore((state) => state.setPatientsSelect);
  const [events, setEvents] = useState<PatientEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const [datasetPickerOpen, setDatasetPickerOpen] = useState(false);
  const datasetPicker = usePaginatedSearch({
    open: datasetPickerOpen,
    fetchPage: fetchDatasetPickerPage,
  });
  const search = datasetPicker.searchValue.trim();
  const [addToDatasetSubmitting, setAddToDatasetSubmitting] = useState(false);
  const [patientDatasets, setPatientDatasets] = useState<PatientDataset[]>([]);
  const [confirmRemoveDataset, setConfirmRemoveDataset] = useState<{ id: string; name: string } | null>(null);
  const [removeDatasetSubmitting, setRemoveDatasetSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<"data" | "documents" | "pipeline">("data");
  const [syncRunning, setSyncRunning] = useState(false);
  const [syncOutcome, setSyncOutcome] = useState<StepOutcome>(null);
  // EuroSpine transfer step (Pipeline tab), independent from sync/reading.
  const eurospine = useEurospineStep(tenantId, patientId, canRunPipeline);

  // Où le curseur est posé, et l'URL le dit.
  //
  // La date vit dans la barre d'adresse parce que trois gestes la font voyager hors de cette page :
  // ajouter un patient à un jeu de données AU 15/06 crée une ligne datée, l'œil de cette ligne
  // rouvre le dossier À CETTE DATE, et « Ouvrir la fiche complète » doit atterrir là où la modale
  // regardait. Un état purement local rendait ce trajet impossible : la ligne savait sa date, la
  // fiche la perdait.
  //
  // Amorcée UNE fois (initialiseur) et réécrite ensuite quand le curseur bouge. Lire l'URL à chaque
  // rendu ferait des deux sens de la synchronisation une boucle.
  const [searchParams, setSearchParams] = useSearchParams();
  const [replayDate, setReplayDate] = useState<string | null>(() =>
    asClinicalDay(searchParams.get("at")),
  );

  useEffect(() => {
    const current = searchParams.get("at") || null;
    if (replayDate === current) return;
    const next = new URLSearchParams(searchParams);
    if (replayDate) next.set("at", replayDate);
    else next.delete("at");
    // `replace` : déplacer le curseur n'est pas une navigation, et trente crans laisseraient
    // trente entrées dans l'historique du navigateur.
    setSearchParams(next, { replace: true });
  }, [replayDate, searchParams, setSearchParams]);
  // L'état daté du dossier et son parcours de soins — tout ce que l'onglet « Dossier » affiche de
  // clinique. Lu ici plutôt que dans `TemporalDossier` parce que l'onglet Pipeline en a besoin
  // aussi : `read_at` est la seule date honnête pour nommer l'étape qui a produit cet écran.
  const projection = useTemporalProjection(tenantId, patientId, replayDate);
  const reloadProjection = projection.reload;

  /** Relire ce dossier par la chaîne doc-first, maintenant.
   *
   *  L'étape qui produit tout ce que cette page affiche était la seule que personne ne pouvait
   *  rejouer : le producteur par lot est à l'échelle du corpus, et le script est un outil
   *  d'exploitation. C'est pourtant le geste qu'on veut juste après avoir changé une ontologie.
   *
   *  Rien n'est rechargé à la main ensuite : la lecture réécrit le journal ET la projection, donc
   *  `loadPatient` suffit à rapporter l'état neuf. */
  const readDossier = async () => {
    setReading(true);
    setReadOutcome(null);
    try {
      const response = (await API.post({
        path: `/patient/${tenantId}/${patientId}/read`,
        body: {},
      })) as { ok?: boolean; skipped_reason?: string | null; error?: string };
      if (response?.ok === false) {
        setReadOutcome("error");
        alert(response.error ?? t`La lecture du dossier a échoué`);
        return;
      }
      // « Inchangé » n'est pas une erreur : c'est l'empreinte qui dit que rien n'a bougé depuis la
      // dernière lecture, et le dire évite qu'un opérateur relance en boucle en croyant à une panne.
      if (response?.skipped_reason) alert(response.skipped_reason);
      reloadDossier();
      setReadOutcome("success");
    } catch {
      setReadOutcome("error");
    } finally {
      setReading(false);
    }
  };

  /** Droit d'accès : tout ce que la plateforme détient sur ce patient, en une archive. */
  const exportDossier = async () => {
    setExporting(true);
    try {
      const status = await downloadPost(
        `/patient/${tenantId}/${patientId}/export`,
        {},
        {},
        `dossier_${patientId}.zip`,
      );
      if (status !== 200) toast.error(t`L'export du dossier a échoué.`);
    } finally {
      setExporting(false);
    }
  };

  /** Opposition : enregistrée, puis les données du patient sont effacées. Irréversible. */
  const recordOpposition = async () => {
    setOppositionSubmitting(true);
    try {
      const response = (await API.post({
        path: `/patient/${tenantId}/${patientId}/opposition`,
        body: {},
      })) as { ok?: boolean; error?: string; detail?: string };
      // A refusal comes back as FastAPI's `{detail}`, without `ok`: only an explicit success counts.
      if (response?.ok !== true) {
        toast.error(
          response?.error ||
            response?.detail ||
            t`L'opposition n'a pas pu être enregistrée.`,
        );
        return;
      }
      toast.success(
        t`Opposition enregistrée : les données du patient ont été effacées.`,
      );
      navigate("/dashboard", { replace: true });
    } finally {
      setOppositionSubmitting(false);
      setConfirmOpposition(false);
    }
  };

  const syncSource = async () => {
    setSyncRunning(true);
    setSyncOutcome(null);
    try {
      const response = await API.post({
        path: "/patient/sync-source",
        query: { tenant_id: tenantId },
      });
      if (response.ok) {
        await loadPatient();
        setSyncOutcome("success");
      } else {
        setSyncOutcome("error");
        alert(response.error || response.message || response.detail);
      }
    } catch {
      setSyncOutcome("error");
      alert(t`Erreur lors de la synchronisation`);
    } finally {
      setSyncRunning(false);
    }
  };

  const loadPatient = useCallback(async () => {
    if (!patientId) return;

    try {
      // La fiche de base est lue depuis l'endpoint prod /patient/{tenant}/{id} : identité,
      // schéma et agenda. Le CLINIQUE, lui, ne vient pas d'ici mais de
      // la projection datée (`useTemporalProjection`), que `TemporalDossier` superpose.
      //
      // Sans la date du curseur, et ce n'est pas un oubli : cet endpoint n'a pas de paramètre
      // `at` — il en recevait un que FastAPI jetait en silence. Le lui envoyer coûtait un
      // rechargement complet de la fiche à chaque cran du curseur, pour une charge utile
      // identique à la précédente.
      const response: PatientGetResponse = await API.get({
        path: `/patient/${tenantId}/${patientId}`,
      });
      if (response.ok && response.data) {
        setSchema(response.data.schema || {});
        setHeaderLayout(response.data.header_layout ?? null);
        // Exactly one dossier per patient.
        const dossier = response.data.patients?.[0] ?? null;
        setPatient(dossier);
        setCorrectionLog(response.data.correction_log ?? []);
        // Seed the current patient into patientsSelect so the header/dashboard
        // patient selector can render its label, also on a direct deep-link.
        if (dossier) {
          const item = mapPatientToPatientsSelectV2(dossier);
          const existing = useMageXStore.getState().patientsSelect ?? [];
          setPatientsSelect([
            item,
            ...existing.filter((p) => !(p.i === item.i && p.c === item.c)),
          ]);
        }
        setEvents(
          [...(response.data.events || [])].sort((a: PatientEvent, b: PatientEvent) => {
            const ta = a.debut ? new Date(a.debut).getTime() : Number.NEGATIVE_INFINITY;
            const tb = b.debut ? new Date(b.debut).getTime() : Number.NEGATIVE_INFINITY;
            return tb - ta;
          }),
        );
      } else {
        // Un lien d'avant la renumérotation (favori, historique) porte encore l'IPP : le serveur
        // dit quel dossier il désigne, s'il existe et si le lecteur peut l'ouvrir.
        const moved = (await API.get({
          path: `/patient/${tenantId}/${patientId}/renumbered`,
        })) as { ok?: boolean; data?: { id_patient?: number } };
        const renumbered = moved?.ok === true ? moved.data?.id_patient : undefined;
        if (renumbered != null) {
          const query = window.location.search;
          navigate(`/patient/${tenantId}/${renumbered}${query}`, { replace: true });
        }
      }
    } catch (error) {
      console.error("Error loading patient:", error);
    } finally {
      setLoading(false);
    }
  }, [patientId, tenantId, setPatientsSelect, navigate]);

  useEffect(() => {
    loadPatient();
  }, [patientId, loadPatient, i18n.locale]);

  /** Relire le dossier ENTIER après un geste : la fiche et la projection datée.
   *
   *  Les deux, toujours. Une correction ou une validation écrit dans le journal, donc ce que la
   *  page affiche change — mais la projection est une lecture séparée, et la rafraîchir seule
   *  laisserait les pastilles sur l'état d'avant, tandis que rafraîchir la fiche seule laisserait
   *  les valeurs sur l'état d'avant. */
  const reloadDossier = useCallback(() => {
    loadPatient();
    reloadProjection();
  }, [loadPatient, reloadProjection]);

  // What this dossier may not join, said where it is offered (PT-155) — the API refuses it anyway:
  // a reference's dossier enters no work dataset, a work dataset's enters no reference, and a
  // sealed reference admits nothing, nor lets one leave.
  const inReference = patientDatasets.some((pd) => pd.purpose === "reference");
  const inWork = patientDatasets.some((pd) => pd.purpose !== "reference");
  const refusedDatasetReason = (
    d: DatasetPickerItem,
    alreadyIn: boolean,
  ): string | null => {
    if (d.state === "sealed") {
      return t`Référence scellée : elle ne change plus.`;
    }
    if (alreadyIn) return null;
    if (d.purpose !== "reference" && inReference) {
      return t`Ce dossier sert de référence : il n'entre dans aucun jeu de travail.`;
    }
    if (d.purpose === "reference" && inWork) {
      return t`Déjà dans un jeu de travail : ce dossier ne peut plus servir de référence.`;
    }
    return null;
  };

  const fetchPatientDatasets = useCallback(async () => {
    if (!tenantId || !patientId) return;
    const res = await API.get({ path: `/patient/${tenantId}/${patientId}/datasets` });
    if (res?.ok && res.data?.datasets) {
      setPatientDatasets(res.data.datasets as PatientDataset[]);
    } else {
      setPatientDatasets([]);
    }
  }, [tenantId, patientId]);

  useEffect(() => {
    fetchPatientDatasets();
  }, [fetchPatientDatasets]);

  const handleRemoveFromDataset = async () => {
    if (!confirmRemoveDataset || !patient?.id_magex) return;
    setRemoveDatasetSubmitting(true);
    try {
      await API.delete({
        path: `/dataset/${confirmRemoveDataset.id}/patients/${patient.id_magex}`,
      });
      await fetchPatientDatasets();
    } catch (e) {
      console.error("Error removing patient from dataset:", e);
    } finally {
      setRemoveDatasetSubmitting(false);
      setConfirmRemoveDataset(null);
    }
  };

  const handleAddPatientToDataset = async (datasetId: string) => {
    if (!patient?.id_magex) return;
    setAddToDatasetSubmitting(true);
    try {
      const response = await API.post({
        path: `/dataset/${datasetId}/patients`,
        body: { patient_ids: [patient.id_magex] },
      });
      if (!response.ok) {
        toast.error(
          readRefusal(response)?.message || t`Erreur lors de l'ajout au jeu de données`,
        );
      } else {
        // Curseur sur une date passée : on ajoute le patient À CETTE DATE, c'est-à-dire
        // une ligne d'exemple datée. Le même patient peut ainsi figurer plusieurs fois
        // dans le jeu de données, une ligne par date, chacune évaluée sur les documents
        // disponibles ce jour-là et notée sur la valeur validée pour ce jour-là.
        if (replayDate) {
          const exampleResponse = await API.post({
            path: `/dataset/${datasetId}/examples`,
            body: {
              inputs: { patient_id_magex: patient.id_magex },
              as_of: replayDate,
            },
          });
          if (!exampleResponse.ok) {
            toast.error(
              exampleResponse.error || t`Erreur lors de l'ajout de la ligne datée`,
            );
          }
        }
        await fetchPatientDatasets();
        toast.success(
          replayDate
            ? t`Patient ajouté au jeu de données à la date du ${dayjs(replayDate).format("DD/MM/YYYY")}`
            : t`Patient ajouté au jeu de données avec succès`,
        );
      }
    } catch (e) {
      console.error("Error adding patient to dataset:", e);
      toast.error(t`Erreur lors de l'ajout au jeu de données`);
    } finally {
      setAddToDatasetSubmitting(false);
    }
  };

  // Compute temporal slider bounds from events + patient creation date.
  // Min = earliest event clinical_date ; max = today. Falls back to a 2-year
  // window centered on today if no event date is available.
  const sliderBounds = useMemo(() => {
    const today = dayjs().format("YYYY-MM-DD");
    const eventDates = events
      .map((e) => e.debut)
      .filter((d): d is string => !!d)
      .map((d) => dayjs(d).format("YYYY-MM-DD"))
      .filter((d) => d <= today);
    if (eventDates.length === 0) {
      return {
        min: dayjs().subtract(2, "year").format("YYYY-MM-DD"),
        max: today,
      };
    }
    eventDates.sort();
    return { min: eventDates[0], max: today };
  }, [events]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-slate-200">
          <Trans>Chargement des données…</Trans>
        </div>
      </div>
    );
  }

  if (!patient) {
    const patientSelect = patientsSelect?.find(
      (p) => p.i === Number(patientId),
    );
    if (!patientSelect) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-slate-200">
            <Trans>Patient non trouvé</Trans>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen">
        <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="border-l-4 border-l-primary shadow-lg bg-gradient-to-br from-white to-slate-50/50">
              <CardContent className="p-8">
                <div className="flex items-center mb-3">
                  <h1 className="text-3xl font-bold text-slate-900">
                    {patientSelect.n}
                  </h1>
                </div>
              </CardContent>
            </Card>
          </div>
          {isAdmin && (
            <div className="lg:col-span-1 flex items-center justify-center">
              <Button
                size="sm"
                onClick={readDossier}
                disabled={reading}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {reading ? (
                  <Trans>Lecture en cours…</Trans>
                ) : readOutcome === "success" ? (
                  <Trans>Dossier lu</Trans>
                ) : (
                  <Trans>Lire le dossier</Trans>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      key={JSON.stringify(patient.updated_at)}
    >
      {/* Header bar — same prod template, augmented with the temporal slider
          on the LEFT (replaces dead space) and the dataset dropdown on the
          right (unchanged). */}
      <div className="px-6 py-4 bg-white/80 backdrop-blur-sm border-b border-slate-200/50 shadow-sm">
        <div className="flex flex-wrap gap-x-6 gap-y-4 items-center">
          <PatientTemporalSlider
            minDate={sliderBounds.min}
            maxDate={sliderBounds.max}
            value={replayDate}
            onChange={setReplayDate}
            onReset={() => setReplayDate(null)}
          />
          <PatientDatasetBadges datasets={patientDatasets} />
          <div className="ml-auto flex items-center gap-2">
            <Popover
              open={datasetPickerOpen}
              onOpenChange={setDatasetPickerOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={addToDatasetSubmitting}
                  className="border-slate-200 hover:border-primary/50 h-8 text-xs"
                >
                  <Database className="h-3.5 w-3.5 mr-1" />
                  {addToDatasetSubmitting ? (
                    <Trans>Envoi en cours...</Trans>
                  ) : replayDate ? (
                    <Trans>
                      Ajouter au jeu de données (au{" "}
                      {dayjs(replayDate).format("DD/MM/YYYY")})
                    </Trans>
                  ) : (
                    <Trans>Ajouter au jeu de données</Trans>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className="w-80 p-0 shadow-xl border-slate-200"
              >
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder={t`Rechercher un jeu de données...`}
                    value={datasetPicker.searchValue}
                    onValueChange={datasetPicker.setSearchValue}
                  />
                  <CommandList onScroll={datasetPicker.onScroll}>
                    {datasetPicker.loading &&
                    datasetPicker.items.length === 0 ? (
                      <div className="flex items-center justify-center py-4 text-slate-400 text-sm gap-2">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        <Trans>Chargement...</Trans>
                      </div>
                    ) : (
                      <>
                        <CommandEmpty>
                          {search ? (
                            <Trans>
                              Aucun jeu de données ne correspond à votre
                              recherche «&nbsp;{search}&nbsp;»
                            </Trans>
                          ) : (
                            <Trans>Aucun jeu de données disponible</Trans>
                          )}
                        </CommandEmpty>
                        <CommandGroup>
                          {datasetPicker.items.map((d) => {
                            const alreadyIn = patientDatasets.some(
                              (pd) => pd.id === d.id,
                            );
                            const refused = refusedDatasetReason(d, alreadyIn);
                            return (
                              <CommandItem
                                key={d.id}
                                value={d.id}
                                disabled={refused !== null}
                                onSelect={() => {
                                  setDatasetPickerOpen(false);
                                  // Curseur sur une date : le clic ajoute la ligne datée même si
                                  // le patient est déjà dans le jeu de données — c'est tout
                                  // l'intérêt, une ligne par date. Le retrait reste accessible
                                  // en repositionnant le curseur sur « aujourd'hui ».
                                  if (alreadyIn && !replayDate)
                                    setConfirmRemoveDataset(d);
                                  else handleAddPatientToDataset(d.id);
                                }}
                                className={alreadyIn ? "text-primary" : ""}
                              >
                                <Check
                                  className={`h-4 w-4 mr-2 shrink-0 transition-opacity ${alreadyIn ? "opacity-100" : "opacity-0"}`}
                                />
                                <div className="flex-1 min-w-0">
                                  <span className="block truncate">
                                    {d.name}
                                  </span>
                                  {refused ? (
                                    <p className="text-xs text-slate-500">
                                      {refused}
                                    </p>
                                  ) : (
                                    d.description && (
                                      <p className="text-xs text-slate-400 truncate">
                                        {d.description}
                                      </p>
                                    )
                                  )}
                                </div>
                              </CommandItem>
                            );
                          })}
                          {datasetPicker.loading &&
                            datasetPicker.items.length > 0 && (
                              <div className="flex items-center justify-center py-2 text-slate-400 text-sm">
                                <Loader2 className="h-3 w-3 animate-spin" />
                              </div>
                            )}
                        </CommandGroup>
                      </>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {!isDemoUser && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="icon"
                    variant="outline"
                    aria-label={t`Actions sur le dossier`}
                    className="border-slate-200 hover:border-primary/50 h-8 w-8"
                  >
                    {exporting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <MoreVertical className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="cursor-pointer"
                    disabled={exporting}
                    onClick={exportDossier}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    <Trans>Exporter le dossier du patient</Trans>
                  </DropdownMenuItem>
                  {!readonlyScope && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="cursor-pointer text-red-700 focus:text-red-700"
                        onClick={() => setConfirmOpposition(true)}
                      >
                        <ShieldOff className="mr-2 h-4 w-4" />
                        <Trans>Le patient s'oppose à la réutilisation de ses données</Trans>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Search bar strip — between header and tabs nav, full width. */}
      <PatientSearchBar tenantId={tenantId!} patientId={patientId!} at={replayDate} />

      <AlertDialog
        open={!!confirmRemoveDataset}
        onOpenChange={(open) => { if (!open) setConfirmRemoveDataset(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Retirer du jeu de données</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>
                Voulez-vous retirer ce patient du jeu de données « {confirmRemoveDataset?.name} » ?
              </Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeDatasetSubmitting}>
              <Trans>Annuler</Trans>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveFromDataset}
              disabled={removeDatasetSubmitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {removeDatasetSubmitting ? <Trans>Suppression...</Trans> : <Trans>Retirer</Trans>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirmOpposition}
        onOpenChange={(open) => {
          if (!open && !oppositionSubmitting) setConfirmOpposition(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Enregistrer l'opposition du patient</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                <p>
                  <Trans>
                    Toutes les données de ce patient détenues par la plateforme sont
                    effacées : valeurs, corrections, documents envoyés, parcours. Ses
                    autres dossiers reconnus comme la même personne le sont aussi.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    Le patient ne sera plus réintégré : ni par la synchronisation, ni par
                    un envoi de documents, ni par un formulaire.
                  </Trans>
                </p>
                <p className="font-medium text-slate-900">
                  <Trans>
                    Cette action est définitive. Exportez d'abord le dossier si le patient
                    en a demandé une copie.
                  </Trans>
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={oppositionSubmitting}>
              <Trans>Annuler</Trans>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                recordOpposition();
              }}
              disabled={oppositionSubmitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {oppositionSubmitting ? (
                <Trans>Effacement en cours...</Trans>
              ) : (
                <Trans>Enregistrer et effacer</Trans>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tabs navigation — identical to prod. */}
      <div className="border-b border-slate-200 bg-white">
        <div className="px-6 flex gap-1">
          <button
            onClick={() => setActiveTab("data")}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === "data"
                ? "border-primary text-primary"
                : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
            }`}
          >
            <span className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4" />
              <Trans>Dossier</Trans>
            </span>
          </button>
          <button
            onClick={() => setActiveTab("documents")}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === "documents"
                ? "border-primary text-primary"
                : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
            }`}
          >
            <span className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <Trans>Documents</Trans>
            </span>
          </button>
          <button
            onClick={() => setActiveTab("pipeline")}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === "pipeline"
                ? "border-primary text-primary"
                : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
            }`}
          >
            <span className="flex items-center gap-2">
              <Workflow className="h-4 w-4" />
              <Trans>Pipeline</Trans>
            </span>
          </button>
        </div>
      </div>

      {/* Onglet Dossier — la lecture clinique : profil, épisodes, moments, champs dans leur
          épisode, et la barre qui clôt le dossier. Rendu par `TemporalDossier`, que la modale du
          jeu de données affiche aussi : les deux écrans montrent le même dossier, ou ils n'en
          montrent pas le même. */}
      {activeTab === "data" && (
        <SourceDocumentProvider
          tenantId={tenantId!}
          patientId={patientId!}
        >
          <CorrectionLogContext.Provider value={correctionLog}>
            <TemporalDossier
              tenantId={tenantId!}
              patientId={patientId!}
              patient={patient}
              schema={schema}
              headerLayout={effectiveHeaderLayout}
              projection={projection}
              events={events}
              at={replayDate}
              inDataset={patientDatasets.length > 0}
              loadPatient={reloadDossier}
            />
          </CorrectionLogContext.Provider>
        </SourceDocumentProvider>
      )}

      {/* Tab Content: Documents */}
      {activeTab === "documents" && (
        <div className="p-6">
          <PatientDocuments tenantId={tenantId!} patientId={patientId!} />
        </div>
      )}

      {/* Tab Content: Pipeline */}
      {activeTab === "pipeline" && (
        <PipelineView
          patient={patient}
          onSync={syncSource}
          syncRunning={syncRunning}
          canRunPipeline={canRunPipeline}
          syncOutcome={syncOutcome}
          reading={reading}
          readOutcome={readOutcome}
          dossierReadAt={projection.readAt}
          onReadDossier={readDossier}
          eurospine={eurospine}
        />
      )}
    </div>
  );
}
